import { useRef } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useToast } from "@/components/ui/use-toast";
import type {
  WallAvatar,
  WallBadgeKind,
  WallBasics,
  WallPhoto,
  WallPromptAnswer,
  WallShelfItem,
  WallSong,
  WallSticker,
} from "@somethings-ui/dom";
import { fetchApi } from "@/lib/api";

export const MENTEE_WALL_QUERY_KEY = "mentee-wall";
export const MENTEE_WALL_BADGES_QUERY_KEY = "mentee-wall-badges";

export type WallCardId = "basics" | "song" | "shelf" | "photos" | "prompts";

export type WallBadges = Record<string, WallBadgeKind>;

export interface WallShelf {
  books?: WallShelfItem[];
  shows?: WallShelfItem[];
  games?: WallShelfItem[];
}

export interface WallView {
  background_color: string | null;
  avatar: WallAvatar | null;
  stickers: WallSticker[];
  basics: WallBasics;
  song: WallSong | null;
  shelf: WallShelf;
  photos: WallPhoto[];
  prompts: WallPromptAnswer[];
  hidden_cards: WallCardId[];
  badges: WallBadges;
}

export interface WallBadgesView {
  badges: WallBadges;
  hasUnacked: boolean;
}

export const useMenteeWall = (mentorshipId: string | null) => {
  return useQuery<WallView>({
    queryKey: [MENTEE_WALL_QUERY_KEY, mentorshipId],
    queryFn: async () => {
      const response = await fetchApi<WallView>({
        url: `/v2/walls/mentorship/${mentorshipId}`,
        method: "GET",
      });
      return response.data;
    },
    enabled: !!mentorshipId,
    retry: false,
    staleTime: 5 * 60 * 1000,
  });
};

export const useMenteeWallBadges = (mentorshipId: string | null) => {
  return useQuery<WallBadgesView>({
    queryKey: [MENTEE_WALL_BADGES_QUERY_KEY, mentorshipId],
    queryFn: async () => {
      const response = await fetchApi<WallBadgesView>({
        url: `/v2/walls/mentorship/${mentorshipId}/badges`,
        method: "GET",
      });
      return response.data;
    },
    enabled: !!mentorshipId,
    retry: false,
    staleTime: 60 * 1000,
  });
};

export const useAckWallBadges = (mentorshipId: string | null) => {
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const ackSeq = useRef(0);
  return useMutation({
    mutationFn: async (keys: string[] | "all") => {
      const response = await fetchApi<WallBadgesView>({
        url: `/v2/walls/mentorship/${mentorshipId}/badges/ack`,
        method: "POST",
        data: keys === "all" ? { all: true } : { keys },
      });
      return response.data;
    },
    onMutate: async (keys) => {
      const seq = ++ackSeq.current;
      const wallKey = [MENTEE_WALL_QUERY_KEY, mentorshipId] as const;
      const badgesKey = [MENTEE_WALL_BADGES_QUERY_KEY, mentorshipId] as const;
      await queryClient.cancelQueries({ queryKey: wallKey });
      await queryClient.cancelQueries({ queryKey: badgesKey });
      const previousWall = queryClient.getQueryData<WallView>(wallKey);
      const previousBadges =
        queryClient.getQueryData<WallBadgesView>(badgesKey);
      const dropped = keys === "all" ? null : new Set(keys);
      const shouldDrop = (key: string) => dropped === null || dropped.has(key);
      queryClient.setQueryData<WallView>(wallKey, (current) =>
        current
          ? {
              ...current,
              badges: Object.fromEntries(
                Object.entries(current.badges ?? {}).filter(
                  ([key]) => !shouldDrop(key),
                ),
              ),
            }
          : current,
      );
      queryClient.setQueryData<WallBadgesView>(badgesKey, (current) => {
        if (!current) return current;
        const badges = Object.fromEntries(
          Object.entries(current.badges).filter(([key]) => !shouldDrop(key)),
        );
        return { badges, hasUnacked: Object.keys(badges).length > 0 };
      });
      return { previousWall, previousBadges, seq };
    },
    onError: (_error, _keys, context) => {
      if (context?.seq !== ackSeq.current) {
        queryClient.invalidateQueries({
          queryKey: [MENTEE_WALL_QUERY_KEY, mentorshipId],
        });
        queryClient.invalidateQueries({
          queryKey: [MENTEE_WALL_BADGES_QUERY_KEY, mentorshipId],
        });
      } else {
        if (context?.previousWall) {
          queryClient.setQueryData(
            [MENTEE_WALL_QUERY_KEY, mentorshipId],
            context.previousWall,
          );
        }
        if (context?.previousBadges) {
          queryClient.setQueryData(
            [MENTEE_WALL_BADGES_QUERY_KEY, mentorshipId],
            context.previousBadges,
          );
        }
      }
      toast({
        title: "Couldn't clear that update",
        variant: "destructive",
      });
    },
    onSuccess: (badgesView, _keys, context) => {
      if (context?.seq !== ackSeq.current) return;
      queryClient.setQueryData<WallBadgesView>(
        [MENTEE_WALL_BADGES_QUERY_KEY, mentorshipId],
        badgesView,
      );
    },
  });
};
